/**
 * Sticky Header — scroll-direction tracker.
 *
 * When the body has class `sticky-header`:
 *   • The header-group container becomes position: sticky (via CSS).
 *
 * When `data-hide-announcement="true"` is set on body:
 *   • Measures the announcement bar height and stores it as a CSS variable.
 *   • Scrolling DOWN adds `.header-scrolling-down` to body → hides announcement bar.
 *   • Scrolling UP removes it → reveals announcement bar.
 */

document.addEventListener('DOMContentLoaded', () => {
  const body = document.body;

  // if (!body.classList.contains('sticky-header')) return;
  // if (body.dataset.hideAnnouncement !== 'true') return;

  const announcementBar = document.querySelector('.announcement-bar-wrapper');
  const header = document.querySelector('.header');
  const headerMain = document.querySelector('.header-main');
  const isHomeHeaderTransparent = body.classList.contains(
    'home-header-transparent',
  );
  const configuredHeaderBgColor =
    headerMain?.dataset.headerBgColor?.trim() || '';

  function syncHeaderMainVisualState(scrollY) {
    if (!headerMain) return;

    const isNotTop = scrollY > 70;
    const isDropdownActive = body.classList.contains('header-dropdown-active');

    if (isHomeHeaderTransparent) {
      headerMain.classList.toggle('shadow-xs', isNotTop);
    } else {
      headerMain.classList.add('shadow-xs');
    }

    if (!isHomeHeaderTransparent) return;

    headerMain.style.backgroundColor =
      (isNotTop || isDropdownActive) && configuredHeaderBgColor
        ? configuredHeaderBgColor
        : 'transparent';
  }

  document.addEventListener('header-dropdown:toggle', () => {
    syncHeaderMainVisualState(window.scrollY || 0);
  });

  function getViewportBreakpoint() {
    const width =
      window.innerWidth || document.documentElement.clientWidth || 0;

    if (width >= 1024) return 'desktop';
    if (width >= 768) return 'tablet';
    return 'mobile';
  }

  function setHeightVariables(baseName, height, breakpoint) {
    const value = `${height}px`;

    // Active variable used by existing styles.
    body.style.setProperty(`--${baseName}`, value);
    // Persist measured values into the current breakpoint bucket.
    body.style.setProperty(`--${baseName}-${breakpoint}`, value);
  }

  // Measure heights and apply CSS properties.
  // Every read happens before every write: interleaving them made each
  // setProperty invalidate layout for the next offsetHeight read.
  function updateHeights() {
    const annHeight = announcementBar ? announcementBar.offsetHeight || 0 : 0;
    const headerHeight = header ? header.offsetHeight || 0 : 0;
    const headerMainHeight = headerMain ? headerMain.offsetHeight || 0 : 0;
    const breakpoint = getViewportBreakpoint();

    if (annHeight > 0) {
      setHeightVariables('announcement-bar-height', annHeight, breakpoint);
    }

    if (headerHeight > 0) {
      setHeightVariables('header-height', headerHeight, breakpoint);
    }

    if (headerMainHeight > 0) {
      setHeightVariables('header-main-height', headerMainHeight, breakpoint);
    }

    window.invalidateWakaHeaderMetrics?.();
  }

  let heightsFrame = null;
  function updateHeightsOnFrame() {
    if (heightsFrame !== null) return;

    heightsFrame = requestAnimationFrame(() => {
      heightsFrame = null;
      updateHeights();
    });
  }

  // Initial measurement, then again once webfonts settle — a fixed timeout
  // both raced font loading and landed at an arbitrary point in the frame.
  updateHeightsOnFrame();
  document.fonts?.ready.then(updateHeightsOnFrame);

  window.addEventListener('resize', updateHeightsOnFrame, {
    passive: true,
  });
  window.addEventListener(
    'resize',
    () => {
      syncHeaderMainVisualState(window.scrollY || 0);
    },
    { passive: true },
  );

  let lastScrollY = window.scrollY || 0;
  let ticking = false;

  syncHeaderMainVisualState(lastScrollY);

  function onScroll() {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;

      // Calculate how far we've scrolled since last check
      const delta = currentScrollY - lastScrollY;
      lastScrollY = currentScrollY;
      ticking = false;

      syncHeaderMainVisualState(currentScrollY);

      // Ignore trivial scrolls (like bounce effects)
      if (Math.abs(delta) < 5) return;

      // Only trigger if we scrolled past the header area to avoid jitter
      if (currentScrollY > 100 && delta > 0) {
        body.classList.add('header-scrolling-down');
      } else if (delta < 0 || currentScrollY <= 100) {
        body.classList.remove('header-scrolling-down');
      }
    });
  }

  // Teleport the mobile menu drawer to the body so it escapes any header stacking/clipping contexts
  const mobileDrawer = document.querySelector('menu-drawer');
  if (mobileDrawer) {
    document.body.appendChild(mobileDrawer);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
});

/**
 * <cart-count> Web Component
 *
 * Displays the current cart item count as a badge.
 * Listens for 'cart:updated' events to reactively update
 * the count without a full page reload.
 */
if (!customElements.get('cart-count')) {
  class CartCount extends HTMLElement {
    constructor() {
      super();
      this.badge = null;
    }

    connectedCallback() {
      this.badge = this.querySelector('.cart-count__badge');
      this.update(this.count);

      // Listen for cart update events (e.g. after AJAX add-to-cart)
      this.onCartUpdatedHandler = this.onCartUpdated.bind(this);
      document.addEventListener('cart:updated', this.onCartUpdatedHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdatedHandler);
    }

    static get observedAttributes() {
      return ['count'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'count') {
        this.update(parseInt(newValue, 10) || 0);
      }
    }

    get count() {
      return parseInt(this.getAttribute('count'), 10) || 0;
    }

    set count(value) {
      this.setAttribute('count', value);
    }

    onCartUpdated(event) {
      if (event.detail && typeof event.detail.count !== 'undefined') {
        this.count = event.detail.count;
      }
    }

    update(count) {
      if (!this.badge) return;
      this.badge.textContent = count;
      const badgeParent = this.closest('.header__cart-count') || this;
      if (count === 0) {
        badgeParent.classList.add('hidden');
      } else {
        badgeParent.classList.remove('hidden');
      }
    }
  }
  customElements.define('cart-count', CartCount);
}

/**
 * <header-cart> component
 * Handles the opening of the cart drawer.
 */
if (!customElements.get('header-cart')) {
  class HeaderCart extends HTMLElement {
    constructor() {
      super();
      this.drawerEnabled = this.dataset.drawerEnabled === 'true';
      this.trigger = this.querySelector('[data-cart-trigger]');
    }

    connectedCallback() {
      if (!this.drawerEnabled || !this.trigger) return;
      this.onTriggerClick = (e) => {
        if (!this.trigger.getAttribute('href').includes('/cart')) return;

        if (document.querySelector('[data-cart-page-section]')) {
          return;
        }

        e.preventDefault();
        document.dispatchEvent(new CustomEvent('cart:open'));
      };
      this.trigger.addEventListener('click', this.onTriggerClick);
    }

    disconnectedCallback() {
      if (this.trigger)
        this.trigger.removeEventListener('click', this.onTriggerClick);
    }
  }
  customElements.define('header-cart', HeaderCart);
}

/**
 * <cart-drawer> component
 * Manages the slide-out cart and its AJAX interactions.
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.drawer = this.querySelector('#CartDrawer');
      this.overlay = this.querySelector('#CartDrawerOverlay');
      this.content = this.querySelector('#CartDrawerContent');
      this.loader = this.querySelector('.cart-drawer__loading');
      this.isOpen = false;
      this._initialized = false;
    }

    getClosedTransform() {
      if (!this.drawer) return '100%';

      const computedRight =
        parseFloat(getComputedStyle(this.drawer).right) || 0;
      return `calc(100% + ${computedRight}px)`;
    }

    connectedCallback() {
      // 1. Handle Teleportation
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return; // connectedCallback will fire again after append
      }

      // 2. Prevent Double Initialization
      if (this._initialized) return;
      this._initialized = true;

      // 3. Attach Event Listeners
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close]')) this.close();
      });

      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:updated', (e) => {
        const source = e.detail?.source;
        if (source !== 'cart-drawer' && source !== 'cart-page') {
          this.open();
          this.fetchCart();
        }
      });
    }

    async open() {
      if (this.isOpen) return;
      this.isOpen = true;

      // Unhide first (from display:none), then remove translate in next frame so the CSS transition fires
      this.drawer?.classList.remove('hidden');
      if (this.drawer) {
        this.drawer.style.setProperty(
          '--tw-translate-x',
          this.getClosedTransform(),
        );
      }
      void this.drawer?.offsetHeight; // force reflow so transition fires
      if (this.drawer) {
        requestAnimationFrame(() => {
          if (this.isOpen) {
            this.drawer.style.setProperty('--tw-translate-x', '0px');
          }
        });
      }
      if (this.overlay) {
        this.overlay.classList.remove('hidden');
        this.overlay.classList.remove('pointer-events-none');
        setTimeout(() => this.overlay.classList.remove('opacity-0'), 10);
      }
      window.getWakaBodyScrollLock().lock();

      // If content is empty (e.g. first open), fetch it.
      // Otherwise, we rely on 'cart:updated' or the previous state.
      if (!this.content || this.content.innerHTML.trim() === '') {
        await this.fetchCart();
      }

      this.trackCartView();
    }

    close() {
      this.isOpen = false;
      if (this.drawer) {
        this.drawer.style.setProperty(
          '--tw-translate-x',
          this.getClosedTransform(),
        );
      }
      if (this.overlay) {
        this.overlay.classList.add('opacity-0');
        this.overlay.classList.add('pointer-events-none');
        setTimeout(() => {
          if (!this.isOpen) this.overlay.classList.add('hidden');
        }, 300);
      }
      // Re-hide after the slide-out transition completes so it won't affect viewport overflow
      setTimeout(() => {
        if (!this.isOpen) this.drawer?.classList.add('hidden');
      }, 300);
      window.getWakaBodyScrollLock().unlock();
    }

    trackCartView() {
      if (typeof window.LenszaAnalytics?.trackCartView === 'function') {
        window.LenszaAnalytics.trackCartView('drawer');
        return;
      }

      document.addEventListener(
        'lensza:analytics-ready',
        () => window.LenszaAnalytics?.trackCartView('drawer'),
        { once: true },
      );
    }

    fetchCart() {
      // Coalesce concurrent callers (e.g. open() and a 'cart:updated' handler
      // firing back-to-back) into a single in-flight request. Two overlapping
      // wakaMorph passes over #CartDrawerContent can otherwise recreate the
      // cart-note/TNC inputs after cart-form's connectedCallback already
      // bound their listeners, leaving those controls dead.
      if (this._fetchCartPromise) return this._fetchCartPromise;

      this._fetchCartPromise = this._doFetchCart().finally(() => {
        this._fetchCartPromise = null;
      });

      return this._fetchCartPromise;
    }

    async _doFetchCart() {
      this.loader?.classList.add('active');
      try {
        const resp = await fetch(
          `${window.location.pathname}?sections=cart-drawer`,
        );
        const data = await resp.json();
        const html = data['cart-drawer'];

        if (!html) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('#CartDrawerContent');

        if (newContent && this.content) {
          if (typeof window.wakaMorph === 'function') {
            window.wakaMorph(
              this.content,
              newContent,
              {},
              {
                context: 'cart-drawer',
                root: this.content,
              },
            );
          } else {
            this.content.innerHTML = newContent.innerHTML;
          }
        }
      } catch (err) {
        console.error('Cart fetch failed', err);
      } finally {
        this.loader?.classList.remove('active');
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 * <header-search> Web Component
 *
 * Controls the header search panel state:
 * - open/close via trigger
 * - close via close buttons, overlay, and Escape key
 * - closes other header-search instances when one opens
 */
if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    constructor() {
      super();
      this.trigger = null;
      this.panel = null;
      this.overlay = null;
      this.input = null;
      this.resultsContainer = null;
      this.closeControls = [];
      this.predictiveSearchUrl = this.dataset.predictiveSearchUrl || '';
      this.predictiveSectionId =
        this.dataset.predictiveSearchSectionId ||
        'header-search-predictive-results';
      this.predictiveAbortController = null;
      this.debouncedInputHandler = null;
      this.staticContent = null;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseControlClick = this.onCloseControlClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onPeerOpen = this.onPeerOpen.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onCloseOnNavigate = this.onCloseOnNavigate.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-header-search-trigger]');
      this.panel = this.querySelector('.header-search__panel');
      this.overlay = this.querySelector('.header-search__overlay');

      // Teleport layer elements to body to avoid clipping/stacking issues in header contexts.
      if (this.panel && this.panel.parentElement !== document.body) {
        document.body.appendChild(this.panel);
      }
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }

      this.input = this.panel?.querySelector('input[type="search"]') || null;
      this.resultsContainer =
        this.panel?.querySelector('[data-predictive-search-results]') || null;
      this.staticContent =
        this.panel?.querySelector('[data-header-search-static]') || null;

      this.closeControls = [
        ...Array.from(this.querySelectorAll('[data-header-search-close]')),
        ...Array.from(
          this.panel?.querySelectorAll('[data-header-search-close]') || [],
        ),
      ];
      if (this.overlay) {
        this.closeControls.push(this.overlay);
      }

      this.trigger?.addEventListener('click', this.onTriggerClick);
      this.input?.addEventListener('input', this.onInputChange);
      this.closeControls.forEach((element) => {
        element.addEventListener('click', this.onCloseControlClick);
      });

      document.addEventListener('header-search:open', this.onPeerOpen);
      document.addEventListener(
        'product-card:navigate',
        this.onCloseOnNavigate,
      );
      document.addEventListener(
        'product-quick-view:navigate',
        this.onCloseOnNavigate,
      );
      document.addEventListener(
        'cart:pre-animate-to-cart',
        this.onCloseOnNavigate,
      );

      this.syncLayerState(this.hasAttribute('open'));
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.input?.removeEventListener('input', this.onInputChange);
      this.closeControls.forEach((element) => {
        element.removeEventListener('click', this.onCloseControlClick);
      });
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('header-search:open', this.onPeerOpen);
      document.removeEventListener(
        'product-card:navigate',
        this.onCloseOnNavigate,
      );
      document.removeEventListener(
        'product-quick-view:navigate',
        this.onCloseOnNavigate,
      );
      document.removeEventListener(
        'cart:pre-animate-to-cart',
        this.onCloseOnNavigate,
      );
      this.predictiveAbortController?.abort();

      if (this.panel && this.panel.parentElement === document.body) {
        this.panel.remove();
      }
      if (this.overlay && this.overlay.parentElement === document.body) {
        this.overlay.remove();
      }
    }

    static get observedAttributes() {
      return ['open'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'open' || oldValue === newValue) return;

      const isOpen = newValue !== null;
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      this.syncLayerState(isOpen);

      if (isOpen) {
        document.body.classList.add('header-search-open');
      } else if (!document.querySelector('header-search[open]')) {
        document.body.classList.remove('header-search-open');
      }
    }

    syncLayerState(isOpen) {
      this.overlay?.classList.toggle('invisible', !isOpen);
      this.overlay?.classList.toggle('opacity-0', !isOpen);

      this.panel?.classList.toggle('invisible', !isOpen);
      this.panel?.classList.toggle('opacity-0', !isOpen);
      this.panel?.classList.toggle('-translate-y-full', !isOpen);

      if (!isOpen) {
        this.setResultsMarkup('');
      }
    }

    onInputChange(event) {
      const searchTerm = event.target.value.trim();

      clearTimeout(this.debouncedInputHandler);
      this.debouncedInputHandler = window.setTimeout(() => {
        if (!searchTerm.length) {
          this.setResultsMarkup('');
          return;
        }

        this.getPredictiveSearchResults(searchTerm);
      }, 250);
    }

    async getPredictiveSearchResults(searchTerm) {
      if (!this.predictiveSearchUrl || !this.resultsContainer) return;

      this.predictiveAbortController?.abort();
      this.predictiveAbortController = new AbortController();

      const url = `${this.predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=${this.predictiveSectionId}&resources[type]=query,product,collection,page,article&resources[limit]=4&resources[limit_scope]=each&options[prefix]=last`;

      try {
        const response = await fetch(url, {
          signal: this.predictiveAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Predictive search failed: ${response.status}`);
        }

        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector(
          `#shopify-section-${this.predictiveSectionId}`,
        );

        this.setResultsMarkup(section ? section.innerHTML.trim() : '');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          this.setResultsMarkup('');
        }
      }
    }

    setResultsMarkup(markup) {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML = markup;
      const hasMarkup = markup.trim().length > 0;
      this.resultsContainer.classList.toggle('hidden', !hasMarkup);
      this.staticContent?.classList.toggle('hidden', hasMarkup);
      this.input?.setAttribute('aria-expanded', hasMarkup ? 'true' : 'false');
    }

    onCloseControlClick(event) {
      event.preventDefault();
      this.close();
    }

    onTriggerClick(event) {
      event.preventDefault();
      if (this.hasAttribute('open')) {
        this.close();
      } else {
        this.open();
      }
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    }

    onPeerOpen(event) {
      if (event.target !== this) {
        this.close();
      }
    }

    onCloseOnNavigate() {
      this.close();
    }

    open() {
      if (this.hasAttribute('open')) return;
      this.setAttribute('open', '');
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.dispatchEvent(
        new CustomEvent('header-search:open', { bubbles: true }),
      );

      const input = this.input;
      if (input) {
        window.setTimeout(() => {
          input.focus({ preventScroll: true });
          input.select();
        }, 40);
      }
    }

    close() {
      if (!this.hasAttribute('open')) return;
      this.predictiveAbortController?.abort();
      if (this.input) {
        this.input.value = '';
      }
      this.setResultsMarkup('');
      this.removeAttribute('open');
      document.removeEventListener('keydown', this.onDocumentKeydown);
    }
  }

  customElements.define('header-search', HeaderSearch);
}
